import { useState, useEffect } from 'react';
import { Eye, EyeOff, Check, Info, Cpu, Globe } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { Telemetry } from '@/lib/telemetry';
import { usePermissions } from '@/lib/usePermissions';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import type { AppConfig, ModelOption } from '@/types/api';

const PROVIDERS = [
  { id: 'anthropic', name: 'Anthropic', icon: 'A' },
  { id: 'openai', name: 'OpenAI', icon: 'O' },
  { id: 'ollama', name: 'Ollama / Local', icon: 'L' },
] as const;

// Stable default model per cloud provider, used ONLY when no valid model is
// already selected. Picking a fixed id (not list position) keeps the default
// deterministic — the catalog is sorted newest-first and its order changes as
// models are released/retired, so `models[0]` would silently drift to whatever
// is newest (and usually most expensive). Mirrors backend
// llm_settings._SAFE_DEFAULT_BY_PROVIDER. Ollama is intentionally omitted:
// local models have no per-call cost and the list is the user's own pulled set,
// so newest-first is an acceptable last-resort there. (A2F-1248, PR #670 review)
const DEFAULT_MODEL_BY_PROVIDER: Record<string, string> = {
  anthropic: 'claude-sonnet-4-6',
  openai: 'gpt-4o',
};

// Full IANA timezone list from the browser runtime (no extra dependency).
// Intl.supportedValuesOf is available in all evergreen browsers and Node ≥18.
const _intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
const IANA_TIMEZONES: readonly string[] =
  typeof _intl.supportedValuesOf === 'function'
    ? _intl.supportedValuesOf('timeZone')
    : ['UTC'];

export function ManageAppPage() {
  const { refreshAuth } = useAuth();
  const { canManageSettings } = usePermissions();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // LLM form state
  const [provider, setProvider] = useState('anthropic');
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({ anthropic: '', openai: '' });
  const [apiKeyDirty, setApiKeyDirty] = useState<Record<string, boolean>>({ anthropic: false, openai: false });
  const [showKey, setShowKey] = useState(false);
  const [model, setModel] = useState('');
  const [models, setModels] = useState<ModelOption[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // A2F-1019: org display timezone state.
  // timezoneDirty mirrors apiKeyDirty: only send display_timezone in PUT when
  // the admin has explicitly interacted with the picker so an org with no TZ
  // configured stays unset (→ labeled UTC) until a deliberate choice is made.
  const [timezone, setTimezone] = useState<string>('');
  const [timezoneDirty, setTimezoneDirty] = useState(false);

  // Load config on mount
  useEffect(() => {
    api.settings.get()
      .then((cfg) => {
        setConfig(cfg);
        setProvider(cfg.llmProvider);
        setApiKeys({ anthropic: cfg.anthropicApiKey, openai: cfg.openaiApiKey });
        setModel(cfg.llmModel);
        // Pre-fill with saved TZ or browser-detected TZ (browser value is
        // a display default only — never the runtime source of truth).
        setTimezone(cfg.displayTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Load models when provider changes
  useEffect(() => {
    setModelsLoading(true);
    api.settings.models(provider)
      .then((m) => {
        setModels(m);
        // Select by model *identity*, never by list position. Order is
        // newest-first and shifts whenever a model ships/retires, so choosing by
        // index would drift. Use a functional update to read the LIVE model — the
        // old code closed over `model` (initial '') so it ALWAYS thought "not in
        // list" and snapped to `models[0]` (newest), silently overwriting the
        // user's saved choice (A2F-1248). Priority: keep current if offered →
        // stable per-provider default → first available (last resort).
        const modelIds = m.map((x) => x.id);
        const fallback = DEFAULT_MODEL_BY_PROVIDER[provider];
        setModel((prev) => {
          if (modelIds.includes(prev)) return prev;
          if (fallback && modelIds.includes(fallback)) return fallback;
          return m[0]?.id || '';
        });
      })
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, [provider]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const body: Record<string, string> = {
        llm_provider: provider,
        llm_model: model,
      };
      if (timezoneDirty) {
        body.display_timezone = timezone;
      }
      if (apiKeyDirty.anthropic) {
        body.anthropic_api_key = apiKeys.anthropic;
      }
      if (apiKeyDirty.openai) {
        body.openai_api_key = apiKeys.openai;
      }
      const updated = await api.settings.update(body);
      setConfig(updated);
      setApiKeys({ anthropic: updated.anthropicApiKey, openai: updated.openaiApiKey });
      setApiKeyDirty({ anthropic: false, openai: false });
      setTimezoneDirty(false);
      // Propagate new TZ into ActiveOrgContext → all formatInstant callsites
      // re-render without a hard reload (BRIEF §Hidden complexity 1).
      await refreshAuth();
      setSaved(true);
      Telemetry.llmSettingsChanged({ provider });
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading settings..." />;

  const isOllama = provider === 'ollama';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Settings
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal mt-1">Manage App</h1>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Organization Timezone Card (A2F-1019) */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-brand-orange" />
          <h2 className="text-base font-semibold text-brand-charcoal">Organization Timezone</h2>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600" htmlFor="tz-select">
            Display timezone
          </label>
          <select
            id="tz-select"
            value={timezone}
            onChange={(e) => { setTimezone(e.target.value); setTimezoneDirty(true); }}
            disabled={!canManageSettings}
            title={!canManageSettings ? 'Requires Admin role' : undefined}
            className={`w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white${!canManageSettings ? ' opacity-50 cursor-not-allowed' : ''}`}
          >
            {IANA_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
          <p className="text-xs text-slate-400">
            Used to display timestamps consistently across the app and audit reports.
          </p>
        </div>
      </div>

      {/* LLM Configuration Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-brand-orange" />
          <h2 className="text-base font-semibold text-brand-charcoal">LLM Configuration</h2>
        </div>

        {/* Provider Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600">Provider</label>
          <div className="grid grid-cols-3 gap-3">
            {PROVIDERS.map((p) => (
              <button
                key={p.id}
                onClick={() => setProvider(p.id)}
                disabled={!canManageSettings}
                title={!canManageSettings ? 'Requires Admin role' : undefined}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                  provider === p.id
                    ? 'border-brand-orange bg-orange-50/50'
                    : 'border-slate-200 hover:border-slate-300'
                }${!canManageSettings ? ' opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                  provider === p.id
                    ? 'bg-brand-orange text-white'
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  {p.icon}
                </span>
                <span className={`text-sm font-medium ${
                  provider === p.id ? 'text-brand-charcoal' : 'text-slate-600'
                }`}>
                  {p.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* API Key */}
        {!isOllama && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600">
              {provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} API Key
            </label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKeys[provider] ?? ''}
                onChange={(e) => {
                  setApiKeys((prev) => ({ ...prev, [provider]: e.target.value }));
                  setApiKeyDirty((prev) => ({ ...prev, [provider]: true }));
                }}
                disabled={!canManageSettings}
                title={!canManageSettings ? 'Requires Admin role' : undefined}
                placeholder={`Enter your ${provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} API key`}
                className={`w-full text-sm border border-slate-200 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange font-mono${!canManageSettings ? ' opacity-50 cursor-not-allowed' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Your key is stored on your local server and never sent externally.
            </p>
          </div>
        )}

        {/* Model Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600">Model</label>
          {modelsLoading ? (
            <div className="text-sm text-slate-400">Loading models...</div>
          ) : (
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={!canManageSettings}
              title={!canManageSettings ? 'Requires Admin role' : undefined}
              className={`w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white${!canManageSettings ? ' opacity-50 cursor-not-allowed' : ''}`}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving || !canManageSettings}
            title={!canManageSettings ? 'Requires Admin role' : undefined}
            className={`flex items-center gap-2 px-5 py-2 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50${!canManageSettings ? ' cursor-not-allowed' : ''}`}
          >
            {saving ? 'Saving...' : saved ? (
              <><Check className="w-4 h-4" /> Saved</>
            ) : 'Save Settings'}
          </button>
          {saved && (
            <span className="text-sm text-emerald-600 font-medium">Settings saved successfully</span>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 flex items-start gap-3">
        <Info className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-slate-600">
            LLM integration powers <strong>Chrona Review</strong> and <strong>Chrona Documents</strong>.
            Changes take effect on the next analysis run.
          </p>
        </div>
      </div>
    </div>
  );
}
